// A settings section as a file. The section is named inside it because a save is a
// REPLACE: a `models` file loaded over `providers` would delete every provider row.
//
// `api_key` leaves masked — GET hands out masks, and the section save restores each from the
// stored row — but nothing else is: a credential kept in `extra_headers` or `extra_config`
// travels in the file as stored.

export const groupFileBody = (key, value, stamp) => ({ key, exported_at: stamp, value });

// A masked preview, not a credential. The mask only restores under the row it was
// exported from, so one arriving under a NEW name would be stored as the key itself.
// What the backend's `_mask_key` writes: an elided middle, or bullets for a secret shown not at all.
const MASK_ELIDED = '\u2026';
const MASK_HIDDEN = '\u2022\u2022\u2022';
const masked = (v) => typeof v === 'string' && (v.includes(MASK_ELIDED) || v.trim() === MASK_HIDDEN);

// A value as text with object keys sorted: an export re-serialised by an editor, or a row a tick
// put back, differs only in key order and is the same value.
const canonical = (value) => JSON.stringify(value, (_, v) => (v && typeof v === 'object' && !Array.isArray(v)
  ? Object.fromEntries(Object.keys(v).sort().map((k) => [k, v[k]])) : v));

export const fileNameFor = (key, stamp) => `${key}-${stamp.slice(0, 10)}.json`;

/** Parse an exported section. Returns `{error}` or `{value, added, changed, dropped}`. */
export function readGroupFile(text, key, current = {}) {
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { error: 'That file is not valid JSON.' };
  }
  const object = parsed && typeof parsed === 'object' && !Array.isArray(parsed);
  const enveloped = object && typeof parsed.key === 'string' && parsed.value !== undefined;
  if (enveloped && parsed.key !== key) {
    return { error: `That file is for \`${parsed.key}\`, not \`${key}\`.` };
  }
  // An export with its section name edited out would otherwise load as a bare section whose rows
  // are `exported_at` and `value`, dropping every real row.
  if (object && !enveloped && ['key', 'value', 'exported_at'].some((field) => field in parsed)) {
    return { error: 'That file looks like an export but does not name its section, so it cannot '
      + 'be checked against this one.' };
  }
  // A bare object is accepted too, so a hand-written section still loads.
  const value = enveloped ? parsed.value : parsed;
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { error: 'Expected an object keyed by row name.' };
  }
  const names = Object.keys(value);
  const same = (a, b) => canonical(a) === canonical(b);
  return {
    value,
    added: names.filter((n) => !(n in current)),
    changed: names.filter((n) => n in current && !same(current[n], value[n])),
    dropped: Object.keys(current).filter((n) => !(n in value)),
    // A mask restores only the key it was made from: under a renamed row, or from another org or
    // deployment, it matches nothing and the save refuses it. Named while the file is on screen.
    unmatchedMasks: names.filter((n) => masked(value[n]?.api_key)
                                        && value[n].api_key !== current[n]?.api_key),
  };
}

const maskWarning = (names) => `${names.length} row(s) carry a masked key that matches nothing `
  + `stored here (${names.join(', ')}) — renamed, or exported from another org or deployment — so `
  + `paste the real key before saving.`;

export const summarise = ({ value, added, changed, dropped, unmatchedMasks = [] }) => {
  const head = `Loaded ${Object.keys(value).length} row(s) — ${added.length} new, `
    + `${changed.length} changed, ${dropped.length} dropped. Review below; nothing is stored `
    + `until you press Save changes.`;
  return unmatchedMasks.length ? `${head} ${maskWarning(unmatchedMasks)}` : head;
};

/** The section as it would be after applying only the ticked changes.
 *  An unticked drop keeps the stored row, which is the whole point: a save is a
 *  replace, and some rows hold credentials that exist nowhere else. */
export function applySelection(current, value, picked) {
  const out = { ...current };
  for (const name of picked.added || []) out[name] = value[name];
  for (const name of picked.changed || []) out[name] = value[name];
  for (const name of picked.dropped || []) delete out[name];
  return out;
}

/** What each row in the review list is doing, for the diff marker. */
export const SIGNS = { added: '+', changed: '~', dropped: '\u2212' };

/** What an apply actually did — counted from the ticked changes, not from the file,
 *  which is a different number the moment anything is unticked. */
export const applied = ({ added, changed, dropped, unmatchedMasks = [] }) => {
  const took = unmatchedMasks.filter((n) => added.includes(n) || changed.includes(n));
  const head = `Applied ${added.length} new, ${changed.length} changed, ${dropped.length} removed. `
    + `Nothing is stored until you press Save changes.`;
  return took.length ? `${head} ${maskWarning(took)}` : head;
};

/** One row's tick flipped, on the section as it stands: rebuilt from the file instead, every edit
 *  made since the import — a pasted key included — was thrown away. */
export function toggleRow(current, { base, value }, kind, name, on) {
  const out = { ...current };
  if (on && kind !== 'dropped') out[name] = value[name];
  else if (!on && name in base) out[name] = base[name];
  else delete out[name];
  return out;
}

/** What the change does to the row: `+` it comes to exist, `−` it stops, `~` it stays and differs.
 *  Read off the rows, not the diff: a file's tombstone is an addition that deletes a built-in, and
 *  dropping an override puts the built-in back rather than removing anything. */
export function signOf(name, { base, value }, hasDefault) {
  const live = (rows) => (name in rows ? !rows[name]?.__deleted : hasDefault);
  const before = live(base);
  const after = live(value);
  if (before === after) return SIGNS.changed;
  return after ? SIGNS.added : SIGNS.dropped;
}

/** `names` grouped by what applying each does, so the note counts what the table marks. */
export function bySign(names, rows, hasDefault) {
  const kind = { [SIGNS.added]: 'added', [SIGNS.changed]: 'changed', [SIGNS.dropped]: 'dropped' };
  const out = { added: [], changed: [], dropped: [] };
  for (const name of names) out[kind[signOf(name, rows, hasDefault(name))]].push(name);
  return out;
}

/** The same rows, whatever order their keys were written in: a tick puts a row back at the end. */
export const sameRows = (a, b) => canonical(a || {}) === canonical(b || {});
